import {
	faCheckCircle,
	faSpinner,
	faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PortalRegisterRequestDto,
	PortalRegisterResponseDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useState } from 'react';
import { submitRegistration } from '../services/portalRegisterService';

/**
 * `'modal'` strips outer padding so the LoginModal's own card chrome
 * shows through; `'page'` adds page-level spacing. Both surfaces share
 * 100% of the form logic via this component.
 */
type Variant = 'modal' | 'page';

interface RegistrationFormProps {
	/**
	 * Portal alias the lead is registering against. Required — the
	 * server validates this against the portal_site table and refuses
	 * submissions for unknown / disabled tenants.
	 */
	tenantAlias: string;
	/** Visual treatment — see Variant. */
	variant?: Variant;
	/** Called after the success screen, when the user clicks "Done". */
	onComplete?: () => void;
	/** Optional cancel button — only shown when provided (e.g. modal back arrow). */
	onCancel?: () => void;
}

interface FormState {
	companyName: string;
	contactFirstName: string;
	contactLastName: string;
	email: string;
	phone: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	zip: string;
	businessType: string;
	federalEin: string;
	leadSource: string;
	notes: string;
	/** Honeypot — kept hidden via CSS. Bots fill it; humans don't. */
	website: string;
}

/**
 * Map server-side field names (DTO keys) to client form keys. Almost
 * everything matches 1:1; only the company-name field uses different
 * naming on the wire vs in the form.
 */
function mapFieldErrors(
	serverErrors: Record<string, string>,
): Record<string, string> {
	const mapped: Record<string, string> = {};
	for (const [key, value] of Object.entries(serverErrors)) {
		if (key === 'name') {
			mapped.companyName = value;
		} else {
			mapped[key] = value;
		}
	}
	return mapped;
}

const EMPTY_FORM: FormState = {
	companyName: '',
	contactFirstName: '',
	contactLastName: '',
	email: '',
	phone: '',
	address1: '',
	address2: '',
	city: '',
	state: '',
	zip: '',
	businessType: '',
	federalEin: '',
	leadSource: '',
	notes: '',
	website: '',
};

export function RegistrationForm({
	tenantAlias,
	variant = 'page',
	onComplete,
	onCancel,
}: RegistrationFormProps): JSX.Element {
	const { t } = useTranslation();
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [bannerError, setBannerError] = useState<string | null>(null);
	const [submittedResult, setSubmittedResult] =
		useState<PortalRegisterResponseDto | null>(null);

	const isModal = variant === 'modal';

	const handleChange =
		<K extends keyof FormState>(field: K) =>
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
			setForm((prev) => ({ ...prev, [field]: e.target.value }));
			if (errors[field] != null) {
				setErrors((prev) => {
					const next = { ...prev };
					delete next[field];
					return next;
				});
			}
		};

	const handleSubmit = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault();
		if (isSubmitting) return;
		setIsSubmitting(true);
		setBannerError(null);
		setErrors({});

		const payload: PortalRegisterRequestDto = {
			tenantAlias,
			name: form.companyName.trim(),
			contactFirstName: form.contactFirstName.trim(),
			contactLastName: form.contactLastName.trim(),
			email: form.email.trim(),
			phone: form.phone.trim(),
			address1: form.address1.trim(),
			address2: form.address2.trim().length > 0 ? form.address2.trim() : null,
			city: form.city.trim(),
			state: form.state.trim(),
			zip: form.zip.trim(),
			businessType:
				form.businessType.trim().length > 0 ? form.businessType.trim() : null,
			federalEin:
				form.federalEin.trim().length > 0 ? form.federalEin.trim() : null,
			leadSource:
				form.leadSource.trim().length > 0 ? form.leadSource.trim() : null,
			notes: form.notes.trim().length > 0 ? form.notes.trim() : null,
			honeypot: form.website,
		};

		try {
			const result = await submitRegistration(payload);
			if (result.success) {
				setSubmittedResult(result);
				return;
			}

			// Map server error codes to a top-of-form banner; field-level
			// errors paint inline.
			if (result.error === 'validation_failed' && result.fieldErrors != null) {
				setErrors(mapFieldErrors(result.fieldErrors));
				setBannerError(t('Please fix the highlighted fields and try again.'));
			} else if (result.error === 'rate_limited') {
				setBannerError(
					t(
						'Too many registration attempts from this network. Please wait a few minutes and try again.',
					),
				);
			} else if (result.error === 'registration_disabled') {
				setBannerError(
					t(
						'Registration is not currently accepted for this storefront. Please contact your sales representative.',
					),
				);
			} else if (result.error === 'origin_not_allowed') {
				setBannerError(
					t(
						'This page is not authorized to submit registrations to that storefront.',
					),
				);
			} else if (result.error === 'tenant_not_found') {
				setBannerError(
					t('We could not match your submission to a known storefront.'),
				);
			} else if (result.error === 'spam_rejected') {
				setBannerError(
					t('Your submission was flagged as automated. Please try again.'),
				);
			} else {
				setBannerError(
					result.message ??
						t('Something went wrong. Please try again in a moment.'),
				);
			}
		} catch (err) {
			setBannerError(
				err instanceof Error
					? err.message
					: t('Network error — please check your connection and retry.'),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (submittedResult != null) {
		return (
			<div
				className={
					isModal
						? 'text-center py-6'
						: 'text-center py-12 px-4 max-w-md mx-auto'
				}
			>
				<div
					className={
						isModal
							? 'mx-auto w-12 h-12 rounded-full bg-portal-accent/20 flex items-center justify-center mb-4'
							: 'mx-auto w-16 h-16 rounded-full bg-portal-accent/15 flex items-center justify-center mb-5'
					}
				>
					<FontAwesomeIcon
						icon={faCheckCircle}
						className={
							isModal
								? 'text-2xl text-portal-accent'
								: 'text-3xl text-portal-accent'
						}
					/>
				</div>
				<h3
					className={
						isModal
							? 'text-base font-semibold text-white mb-2'
							: 'text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3'
					}
				>
					{t('Thanks — we got your information.')}
				</h3>
				<p
					className={
						isModal
							? 'text-sm text-white/75 mb-6'
							: 'text-base text-gray-600 dark:text-gray-400 mb-6'
					}
				>
					{t(
						'Our team will review your registration and reach out shortly to confirm your account and walk through next steps.',
					)}
				</p>
				{onComplete != null && (
					<button
						type="button"
						onClick={onComplete}
						className={
							isModal
								? 'inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold shadow-lg shadow-black/30 transition-colors'
								: 'inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold shadow-lg transition-colors'
						}
					>
						{t('Done')}
					</button>
				)}
			</div>
		);
	}

	// Two visual variants share the same form fields. Modal variant uses
	// the dark glass look (white text on dark) inherited from LoginModal;
	// page variant uses the standard light surface.
	const labelCls = isModal
		? 'block text-sm font-medium text-white/85 mb-1'
		: 'block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1';
	const inputBase = isModal
		? 'w-full px-3 py-2.5 rounded-lg bg-white border border-white/30 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm'
		: 'w-full px-3 py-2.5 rounded-lg bg-brand-surface border border-brand-border text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent';
	const errorCls = 'mt-1 text-xs text-red-400';

	const fieldClass = (field: keyof FormState): string =>
		errors[field] != null
			? `${inputBase} border-red-400 focus:ring-red-400`
			: inputBase;

	return (
		<form
			onSubmit={handleSubmit}
			className={isModal ? 'space-y-3' : 'space-y-4'}
			noValidate
		>
			{bannerError != null && (
				<div
					className={
						isModal
							? 'flex items-start gap-2 bg-red-500/15 border border-red-400/30 text-red-200 text-xs rounded-lg px-3 py-2.5'
							: 'flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2.5'
					}
				>
					<FontAwesomeIcon
						icon={faTriangleExclamation}
						className="mt-0.5 shrink-0"
					/>
					<span>{bannerError}</span>
				</div>
			)}

			<div>
				<label htmlFor="reg-companyName" className={labelCls}>
					{t('Company Name')} <span className="text-portal-accent">*</span>
				</label>
				<input
					id="reg-companyName"
					type="text"
					autoComplete="organization"
					required
					value={form.companyName}
					onChange={handleChange('companyName')}
					disabled={isSubmitting}
					className={fieldClass('companyName')}
				/>
				{errors.companyName != null && (
					<p className={errorCls}>{t(errors.companyName)}</p>
				)}
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div>
					<label htmlFor="reg-firstName" className={labelCls}>
						{t('First Name')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-firstName"
						type="text"
						autoComplete="given-name"
						required
						value={form.contactFirstName}
						onChange={handleChange('contactFirstName')}
						disabled={isSubmitting}
						className={fieldClass('contactFirstName')}
					/>
					{errors.contactFirstName != null && (
						<p className={errorCls}>{t(errors.contactFirstName)}</p>
					)}
				</div>
				<div>
					<label htmlFor="reg-lastName" className={labelCls}>
						{t('Last Name')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-lastName"
						type="text"
						autoComplete="family-name"
						required
						value={form.contactLastName}
						onChange={handleChange('contactLastName')}
						disabled={isSubmitting}
						className={fieldClass('contactLastName')}
					/>
					{errors.contactLastName != null && (
						<p className={errorCls}>{t(errors.contactLastName)}</p>
					)}
				</div>
			</div>

			<div>
				<label htmlFor="reg-email" className={labelCls}>
					{t('Email')} <span className="text-portal-accent">*</span>
				</label>
				<input
					id="reg-email"
					type="email"
					autoComplete="email"
					required
					value={form.email}
					onChange={handleChange('email')}
					disabled={isSubmitting}
					className={fieldClass('email')}
				/>
				{errors.email != null && <p className={errorCls}>{t(errors.email)}</p>}
			</div>

			<div>
				<label htmlFor="reg-phone" className={labelCls}>
					{t('Phone')} <span className="text-portal-accent">*</span>
				</label>
				<input
					id="reg-phone"
					type="tel"
					autoComplete="tel"
					inputMode="tel"
					required
					value={form.phone}
					onChange={handleChange('phone')}
					disabled={isSubmitting}
					className={fieldClass('phone')}
				/>
				{errors.phone != null && <p className={errorCls}>{t(errors.phone)}</p>}
			</div>

			<div>
				<label htmlFor="reg-address1" className={labelCls}>
					{t('Street Address')} <span className="text-portal-accent">*</span>
				</label>
				<input
					id="reg-address1"
					type="text"
					autoComplete="address-line1"
					required
					value={form.address1}
					onChange={handleChange('address1')}
					disabled={isSubmitting}
					className={fieldClass('address1')}
				/>
				{errors.address1 != null && (
					<p className={errorCls}>{t(errors.address1)}</p>
				)}
			</div>

			<div>
				<label htmlFor="reg-address2" className={labelCls}>
					{t('Address Line 2')}
				</label>
				<input
					id="reg-address2"
					type="text"
					autoComplete="address-line2"
					value={form.address2}
					onChange={handleChange('address2')}
					disabled={isSubmitting}
					className={inputBase}
				/>
			</div>

			{/* City / State / ZIP — 4-col grid (2 / 1 / 1) gives State a
			    sane width on narrow viewports. The earlier 6-col grid
			    squeezed State into 1/6 of width, which on iPhone 17
			    (402pt) wrapped the required-asterisk below the label and
			    left the input too narrow to read its 2-char maxLength
			    value. */}
			<div className="grid grid-cols-4 gap-3">
				<div className="col-span-2">
					<label htmlFor="reg-city" className={labelCls}>
						{t('City')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-city"
						type="text"
						autoComplete="address-level2"
						required
						value={form.city}
						onChange={handleChange('city')}
						disabled={isSubmitting}
						className={fieldClass('city')}
					/>
					{errors.city != null && <p className={errorCls}>{t(errors.city)}</p>}
				</div>
				<div className="col-span-1">
					<label htmlFor="reg-state" className={labelCls}>
						{t('State')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-state"
						type="text"
						autoComplete="address-level1"
						required
						maxLength={2}
						value={form.state}
						onChange={handleChange('state')}
						disabled={isSubmitting}
						className={`${fieldClass('state')} uppercase`}
					/>
					{errors.state != null && (
						<p className={errorCls}>{t(errors.state)}</p>
					)}
				</div>
				<div className="col-span-1">
					<label htmlFor="reg-zip" className={labelCls}>
						{t('ZIP')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-zip"
						type="text"
						autoComplete="postal-code"
						required
						value={form.zip}
						onChange={handleChange('zip')}
						disabled={isSubmitting}
						className={fieldClass('zip')}
					/>
					{errors.zip != null && <p className={errorCls}>{t(errors.zip)}</p>}
				</div>
			</div>

			<div>
				<label htmlFor="reg-businessType" className={labelCls}>
					{t('Business Type')}
				</label>
				<input
					id="reg-businessType"
					type="text"
					placeholder={t('Restaurant, grocery, distributor, …')}
					value={form.businessType}
					onChange={handleChange('businessType')}
					disabled={isSubmitting}
					className={inputBase}
				/>
			</div>

			<div>
				<label htmlFor="reg-leadSource" className={labelCls}>
					{t('How did you hear about us?')}
				</label>
				<input
					id="reg-leadSource"
					type="text"
					placeholder={t('Trade show, referral, search, …')}
					value={form.leadSource}
					onChange={handleChange('leadSource')}
					disabled={isSubmitting}
					className={inputBase}
				/>
			</div>

			<div>
				<label htmlFor="reg-notes" className={labelCls}>
					{t('Anything else we should know?')}
				</label>
				<textarea
					id="reg-notes"
					rows={3}
					value={form.notes}
					onChange={handleChange('notes')}
					disabled={isSubmitting}
					className={inputBase}
				/>
			</div>

			{/*
			  Honeypot — visually hidden but present in the DOM. Bots
			  filling every input in sight will populate it; humans never
			  see it. Server rejects any submission with this set.
			  `aria-hidden` + `tabIndex={-1}` keep it off the screen reader
			  + tab path so legitimate accessibility tooling doesn't
			  accidentally fill it.
			*/}
			<div
				aria-hidden="true"
				style={{
					position: 'absolute',
					left: '-9999px',
					height: 0,
					width: 0,
					overflow: 'hidden',
				}}
			>
				<label htmlFor="reg-website-hp">{t('Website')}</label>
				<input
					id="reg-website-hp"
					type="text"
					tabIndex={-1}
					autoComplete="off"
					value={form.website}
					onChange={handleChange('website')}
				/>
			</div>

			<div
				className={
					isModal
						? 'flex items-center gap-2 pt-2'
						: 'flex items-center gap-3 pt-2'
				}
			>
				{onCancel != null && (
					<button
						type="button"
						onClick={onCancel}
						disabled={isSubmitting}
						className={
							isModal
								? 'flex-1 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors'
								: 'flex-1 px-4 py-3 rounded-full text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
						}
					>
						{t('Cancel')}
					</button>
				)}
				<button
					type="submit"
					disabled={isSubmitting}
					className={
						isModal
							? 'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white text-sm font-semibold transition-colors min-h-touch'
							: 'flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white text-sm font-semibold shadow-lg transition-colors min-h-touch'
					}
				>
					{isSubmitting && <FontAwesomeIcon icon={faSpinner} spin />}
					{isSubmitting ? t('Submitting…') : t('Submit Registration')}
				</button>
			</div>

			<p
				className={
					isModal
						? 'text-[11px] text-white/55 leading-relaxed'
						: 'text-xs text-brand-muted leading-relaxed'
				}
			>
				{t(
					'By submitting, you confirm that the contact details above are accurate. Our team will review your information and follow up to set up your account.',
				)}
			</p>
		</form>
	);
}
